import { useState, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { EditPage, EditPagePanel, EditPageSelect, EditPageCheckbox } from '../../../../common/EditPage';
import { useParams, useNavigate } from 'react-router-dom';
import { getOne, getList, create } from '../../../../common/v4/BackendProvider';
import { toast } from 'react-toastify';
import Panel from '../../../../common/layout/Panel';

const AccessRightsBulkEdit = () => {
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [areas, setAreas] = useState([]);
    const [groups, setGroups] = useState([]);
    const [rights, setRights] = useState({});
    const [metadata, setMetadata] = useState({});
    const navigate = useNavigate();
    const { groupId } = useParams();
    
    useEffect(() => {
        // Load groups and areas for the dropdown
        getOne(`/admin/access/rights/`, 'new').then(response => {
            setMetadata(response.data.metadata);
            setGroups(response.data.metadata.groups || {});
            setAreas(Object.entries(response.data.metadata.areas || {}).map(([id, name]) => ({ id, name })));
        }).catch(error => {
            toast.error("Error fetching metadata: " + error.message, {});
        });

        // If groupId is provided, load existing rights for that group
        if (groupId) {
            loadGroupRights(groupId);
        }
    }, [groupId]);

    const loadGroupRights = async (groupId) => {
        try {
            const response = await getOne(`/admin/access/groups/`, groupId);
            setSelectedGroup(response.data.data);
            
            // Load rights for this group
            const rightsResponse = await getList(`/admin/access/rights/`);
            const groupRights = rightsResponse.data.data.filter(right => 
                right.access_group_id == groupId
            );
            
            const rightsMap = {};
            groupRights.forEach(right => {
                rightsMap[right.access_area_id] = {
                    can_read: right.can_read,
                    can_write: right.can_write
                };
            });
            setRights(rightsMap);
        } catch (error) {
            toast.error("Error loading group rights: " + error.message, {});
        }
    };

    const handleGroupChange = (groupId) => {
        if (groupId) {
            loadGroupRights(groupId);
        } else {
            setSelectedGroup(null);
            setRights({});
        }
    };

    const handlePermissionChange = (areaId, permission, value) => {
        setRights(prev => ({
            ...prev,
            [areaId]: {
                ...prev[areaId],
                [permission]: value
            }
        }));
    };

    const handleSave = async () => {
        if (!selectedGroup) {
            toast.error("Please select an access group", {});
            return;
        }

        const rightsData = areas.map(area => ({
            access_area_id: area.id,
            can_read: rights[area.id]?.can_read || false,
            can_write: rights[area.id]?.can_write || false
        }));

        try {
            await create(`/admin/access/rights/bulk-update`, {
                access_group_id: selectedGroup.id,
                rights: rightsData
            });
            toast.success("Access rights updated successfully", {});
            navigate(`/admin/access/rights`);
        } catch (error) {
            toast.error("Error updating access rights: " + error.message, {});
        }
    };

    const handleCancel = () => {
        navigate(`/admin/access/rights`);
    };

    return (
        <>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                closeOnClick={true}
                pauseOnHover={true}
                draggable={true}
                progress="undefined"
            />
            <EditPage handleSave={handleSave} handleCancel={handleCancel}>
                <EditPagePanel>
                    <EditPageSelect 
                        label="Access Group" 
                        id="access_group_id" 
                        onChange={(id, value) => handleGroupChange(value)}
                        value={selectedGroup?.id || ''}
                        options={groups}
                        required
                    /><div></div>
                </EditPagePanel>
                
                <EditPagePanel>
                    <div></div>
                    {selectedGroup ? (
                        <Panel title={`Access Rights for ${selectedGroup.name}`}>
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white border border-gray-300">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="px-4 py-2 border-b text-left">Access Area</th>
                                            <th className="px-4 py-2 border-b text-center">Can Read</th>
                                            <th className="px-4 py-2 border-b text-center">Can Write</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {areas.map(area => (
                                            <tr key={area.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-2 border-b">
                                                    <div>
                                                        <div className="font-medium">{area.name}</div>
                                                        <div className="text-sm text-gray-500">
                                                            {metadata.areas?.[area.id] || ''}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-2 border-b text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={rights[area.id]?.can_read || false}
                                                        onChange={(e) => handlePermissionChange(area.id, 'can_read', e.target.checked)}
                                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                </td>
                                                <td className="px-4 py-2 border-b text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={rights[area.id]?.can_write || false}
                                                        onChange={(e) => handlePermissionChange(area.id, 'can_write', e.target.checked)}
                                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Panel>
                    ) : (
                        <div className="p-6 text-center text-gray-500">
                            Please select an access group to manage rights
                        </div>
                    )}
                </EditPagePanel>
            </EditPage>
        </>
    )
}

export default AccessRightsBulkEdit; 